import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck, Search, Check, X, AlertTriangle, User, Car, Building2, Upload, FileImage } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VendorsPage = () => {
  const { user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [uploadingImage, setUploadingImage] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    pan_number: "",
    gst_number: "",
    vehicle_types: [],
    status: "active",
    drivers: [],
    vehicles: [],
    bank_accounts: [],
    pan_image_url: "",
    gst_certificate_url: "",
    business_registration_url: "",
    cheque_image_url: "",
    passbook_image_url: ""
  });
  
  // New driver/vehicle/bank states
  const [newDriver, setNewDriver] = useState({ name: "", mobile: "", license_number: "", license_image_url: "" });
  const [newVehicle, setNewVehicle] = useState({ vehicle_number: "", vehicle_type: "", capacity: "" });
  const [newBankAccount, setNewBankAccount] = useState({ 
    account_name: "", 
    account_number: "", 
    bank_name: "", 
    ifsc_code: "", 
    branch: "" 
  });

  const canVerify = user?.role === "admin" || user?.role === "super_admin";
  const canReject = user?.role === "admin" || user?.role === "super_admin";
  const canDelete = user?.role === "admin" || user?.role === "super_admin";

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API_URL}/vendors`);
      setVendors(response.data);
    } catch (error) {
      toast.error("Failed to fetch vendors");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedVendor) {
        await axios.put(`${API_URL}/vendors/${selectedVendor.id}`, formData);
        toast.success("Vendor updated successfully");
      } else {
        await axios.post(`${API_URL}/vendors`, formData);
        toast.success("Vendor created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleVerify = async (vendorId) => {
    try {
      await axios.post(`${API_URL}/vendors/${vendorId}/verify`);
      toast.success("Vendor verified successfully");
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API_URL}/vendors/${selectedVendor.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success("Vendor rejected");
      setRejectDialogOpen(false);
      setSelectedVendor(null);
      setRejectReason("");
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rejection failed");
    }
  };

  const handleEdit = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      name: vendor.name,
      contact_person: vendor.contact_person,
      email: vendor.email,
      phone: vendor.phone,
      address: vendor.address,
      pan_number: vendor.pan_number || "",
      gst_number: vendor.gst_number || "",
      vehicle_types: vendor.vehicle_types || [],
      status: vendor.status,
      drivers: vendor.drivers || [],
      vehicles: vendor.vehicles || [],
      bank_accounts: vendor.bank_accounts || [],
      pan_image_url: vendor.pan_image_url || "",
      gst_certificate_url: vendor.gst_certificate_url || "",
      business_registration_url: vendor.business_registration_url || "",
      cheque_image_url: vendor.cheque_image_url || "",
      passbook_image_url: vendor.passbook_image_url || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/vendors/${selectedVendor.id}`);
      toast.success("Vendor deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedVendor(null);
      fetchVendors();
    } catch (error) {
      toast.error("Failed to delete vendor");
    }
  };

  const resetForm = () => {
    setSelectedVendor(null);
    setFormData({
      name: "",
      contact_person: "",
      email: "",
      phone: "",
      address: "",
      pan_number: "",
      gst_number: "",
      vehicle_types: [],
      status: "active",
      drivers: [],
      vehicles: [],
      bank_accounts: [],
      pan_image_url: "",
      gst_certificate_url: "",
      business_registration_url: "",
      cheque_image_url: "",
      passbook_image_url: ""
    });
    setNewDriver({ name: "", mobile: "", license_number: "" });
    setNewVehicle({ vehicle_number: "", vehicle_type: "", capacity: "" });
    setNewBankAccount({ account_name: "", account_number: "", bank_name: "", ifsc_code: "", branch: "" });
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadingImage(fieldName);
    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    
    try {
      const response = await axios.post(`${API_URL}/upload-image`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setFormData({ ...formData, [fieldName]: response.data.url });
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(null);
    }
  };

  const addDriver = () => {
    if (newDriver.name && newDriver.mobile) {
      setFormData({
        ...formData,
        drivers: [...formData.drivers, { ...newDriver }]
      });
      setNewDriver({ name: "", mobile: "", license_number: "", license_image_url: "" });
    }
  };

  const removeDriver = (index) => {
    setFormData({
      ...formData,
      drivers: formData.drivers.filter((_, i) => i !== index)
    });
  };

  const addVehicle = () => {
    if (newVehicle.vehicle_number && newVehicle.vehicle_type) {
      setFormData({
        ...formData,
        vehicles: [...formData.vehicles, { ...newVehicle }]
      });
      setNewVehicle({ vehicle_number: "", vehicle_type: "", capacity: "" });
    }
  };

  const removeVehicle = (index) => {
    setFormData({
      ...formData,
      vehicles: formData.vehicles.filter((_, i) => i !== index)
    });
  };

  const addBankAccount = () => {
    if (newBankAccount.account_number && newBankAccount.bank_name && newBankAccount.ifsc_code) {
      setFormData({
        ...formData,
        bank_accounts: [...formData.bank_accounts, { ...newBankAccount }]
      });
      setNewBankAccount({ account_name: "", account_number: "", bank_name: "", ifsc_code: "", branch: "" });
      toast.success("Bank account added");
    } else {
      toast.error("Please fill Account Number, Bank Name, and IFSC Code");
    }
  };

  const removeBankAccount = (index) => {
    setFormData({
      ...formData,
      bank_accounts: formData.bank_accounts.filter((_, i) => i !== index)
    });
  };

  const getVerificationBadge = (status) => {
    const styles = {
      pending: "border-yellow-500/50 text-yellow-400 bg-yellow-500/10",
      verified: "border-green-500/50 text-green-400 bg-green-500/10",
      rejected: "border-red-500/50 text-red-400 bg-red-500/10"
    };
    return styles[status] || styles.pending;
  };

  const filteredVendors = vendors.filter(vendor =>
    vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading vendors...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vendors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <Truck className="w-8 h-8 text-primary" />
            Vendor Management
          </h1>
          <p className="text-muted-foreground mt-1">Manage your transport vendors and partners</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="glow-primary bg-primary hover:bg-primary/90"
          data-testid="add-vendor-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Vendor
        </Button>
      </div>

      {/* Search */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search vendors by name, contact person, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-secondary/50 border-border"
              data-testid="vendor-search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Vendors ({filteredVendors.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Vendor Name</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Contact Person</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Email</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Phone</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Verification</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredVendors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No vendors found. Add your first vendor to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredVendors.map((vendor) => (
                    <TableRow key={vendor.id} className="border-border table-row-hover" data-testid={`vendor-row-${vendor.id}`}>
                      <TableCell className="font-medium text-foreground">{vendor.name}</TableCell>
                      <TableCell className="text-muted-foreground">{vendor.contact_person}</TableCell>
                      <TableCell className="text-muted-foreground font-mono text-sm">{vendor.email}</TableCell>
                      <TableCell className="text-muted-foreground font-mono">{vendor.phone}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getVerificationBadge(vendor.verification_status || "pending")}>
                          {vendor.verification_status || "pending"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline" 
                          className={vendor.status === "active" 
                            ? "border-green-500/50 text-green-400 bg-green-500/10" 
                            : "border-red-500/50 text-red-400 bg-red-500/10"
                          }
                        >
                          {vendor.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canVerify && (vendor.verification_status === "pending" || !vendor.verification_status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-green-400 hover:text-green-300"
                              onClick={() => handleVerify(vendor.id)}
                              data-testid={`verify-vendor-${vendor.id}`}
                              title="Verify"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {canReject && (vendor.verification_status === "pending" || !vendor.verification_status) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => { setSelectedVendor(vendor); setRejectDialogOpen(true); }}
                              data-testid={`reject-vendor-${vendor.id}`}
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(vendor)}
                            data-testid={`edit-vendor-${vendor.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => { setSelectedVendor(vendor); setDeleteDialogOpen(true); }}
                              data-testid={`delete-vendor-${vendor.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedVendor ? "Edit Vendor" : "Add New Vendor"}
            </DialogTitle>
            <DialogDescription>
              {selectedVendor ? "Update vendor information" : "Enter the details of the new vendor"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Vendor Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Express Transport Co."
                required
                data-testid="vendor-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_person">Contact Person</Label>
              <Input
                id="contact_person"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="Rajesh Kumar"
                required
                data-testid="vendor-contact-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="contact@express.com"
                  required
                  data-testid="vendor-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="+91 98765 43210"
                  required
                  data-testid="vendor-phone-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="bg-secondary/50 border-border"
                placeholder="123 Logistics Park, Mumbai"
                required
                data-testid="vendor-address-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pan_number">PAN Number</Label>
                <Input
                  id="pan_number"
                  value={formData.pan_number}
                  onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                  className="bg-secondary/50 border-border"
                  placeholder="ABCDE1234F"
                  data-testid="vendor-pan-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gst_number">GST Number <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                <Input
                  id="gst_number"
                  value={formData.gst_number}
                  onChange={(e) => setFormData({ ...formData, gst_number: e.target.value.toUpperCase() })}
                  className="bg-secondary/50 border-border"
                  placeholder="27ABCDE1234F1Z5"
                  data-testid="vendor-gst-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="vendor-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Document Images Section */}
            <div className="p-3 bg-secondary/30 rounded-lg space-y-3" data-testid="vendor-documents-section">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <FileImage className="w-4 h-4" /> Document Images
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {/* PAN Card Image */}
                <div className="space-y-1">
                  <Label className="text-xs">PAN Card</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "pan_image_url")}
                      className="hidden"
                      id="pan_image_upload"
                      data-testid="vendor-pan-image-input"
                    />
                    <label
                      htmlFor="pan_image_upload"
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-secondary/50 border border-border rounded-md cursor-pointer hover:bg-secondary/70 text-sm ${uploadingImage === "pan_image_url" ? "opacity-50" : ""}`}
                    >
                      <Upload className="w-4 h-4" />
                      {formData.pan_image_url ? "Change" : "Upload"}
                    </label>
                    {formData.pan_image_url && (
                      <a href={formData.pan_image_url} target="_blank" rel="noreferrer" className="text-primary text-xs underline">View</a>
                    )}
                  </div>
                </div>
                
                {/* GST Certificate */}
                <div className="space-y-1">
                  <Label className="text-xs">GST Certificate</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "gst_certificate_url")}
                      className="hidden"
                      id="gst_cert_upload"
                      data-testid="vendor-gst-image-input"
                    />
                    <label
                      htmlFor="gst_cert_upload"
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-secondary/50 border border-border rounded-md cursor-pointer hover:bg-secondary/70 text-sm ${uploadingImage === "gst_certificate_url" ? "opacity-50" : ""}`}
                    >
                      <Upload className="w-4 h-4" />
                      {formData.gst_certificate_url ? "Change" : "Upload"}
                    </label>
                    {formData.gst_certificate_url && (
                      <a href={formData.gst_certificate_url} target="_blank" rel="noreferrer" className="text-primary text-xs underline">View</a>
                    )}
                  </div>
                </div>
                
                {/* Business Registration */}
                <div className="space-y-1">
                  <Label className="text-xs">Business Registration</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "business_registration_url")}
                      className="hidden"
                      id="business_reg_upload"
                      data-testid="vendor-business-reg-input"
                    />
                    <label
                      htmlFor="business_reg_upload"
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-secondary/50 border border-border rounded-md cursor-pointer hover:bg-secondary/70 text-sm ${uploadingImage === "business_registration_url" ? "opacity-50" : ""}`}
                    >
                      <Upload className="w-4 h-4" />
                      {formData.business_registration_url ? "Change" : "Upload"}
                    </label>
                    {formData.business_registration_url && (
                      <a href={formData.business_registration_url} target="_blank" rel="noreferrer" className="text-primary text-xs underline">View</a>
                    )}
                  </div>
                </div>
                
                {/* Cheque Image */}
                <div className="space-y-1">
                  <Label className="text-xs">Cheque Image</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "cheque_image_url")}
                      className="hidden"
                      id="cheque_upload"
                      data-testid="vendor-cheque-image-input"
                    />
                    <label
                      htmlFor="cheque_upload"
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-secondary/50 border border-border rounded-md cursor-pointer hover:bg-secondary/70 text-sm ${uploadingImage === "cheque_image_url" ? "opacity-50" : ""}`}
                    >
                      <Upload className="w-4 h-4" />
                      {formData.cheque_image_url ? "Change" : "Upload"}
                    </label>
                    {formData.cheque_image_url && (
                      <a href={formData.cheque_image_url} target="_blank" rel="noreferrer" className="text-primary text-xs underline">View</a>
                    )}
                  </div>
                </div>
                
                {/* Passbook Image */}
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Passbook Image</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, "passbook_image_url")}
                      className="hidden"
                      id="passbook_upload"
                      data-testid="vendor-passbook-image-input"
                    />
                    <label
                      htmlFor="passbook_upload"
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-secondary/50 border border-border rounded-md cursor-pointer hover:bg-secondary/70 text-sm ${uploadingImage === "passbook_image_url" ? "opacity-50" : ""}`}
                    >
                      <Upload className="w-4 h-4" />
                      {formData.passbook_image_url ? "Change" : "Upload"}
                    </label>
                    {formData.passbook_image_url && (
                      <a href={formData.passbook_image_url} target="_blank" rel="noreferrer" className="text-primary text-xs underline">View</a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Drivers Section */}
            <div className="p-3 bg-secondary/30 rounded-lg space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <User className="w-4 h-4" /> Drivers ({formData.drivers.length})
              </h4>
              
              {formData.drivers.length > 0 && (
                <div className="space-y-2">
                  {formData.drivers.map((driver, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-secondary/50 rounded text-sm">
                      <div className="flex flex-col">
                        <span className="font-medium">{driver.name} - {driver.mobile}</span>
                        <span className="text-xs text-muted-foreground">
                          License: {driver.license_number || 'N/A'}
                          {driver.license_image_url && (
                            <a href={driver.license_image_url} target="_blank" rel="noreferrer" className="text-primary ml-2 hover:underline">View Image</a>
                          )}
                        </span>
                      </div>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeDriver(index)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="Driver Name *"
                    value={newDriver.name}
                    onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })}
                    className="bg-secondary/50 h-8 text-sm"
                  />
                  <Input
                    placeholder="Mobile *"
                    value={newDriver.mobile}
                    onChange={(e) => setNewDriver({ ...newDriver, mobile: e.target.value })}
                    className="bg-secondary/50 h-8 text-sm"
                  />
                  <Input
                    placeholder="License Number"
                    value={newDriver.license_number}
                    onChange={(e) => setNewDriver({ ...newDriver, license_number: e.target.value.toUpperCase() })}
                    className="bg-secondary/50 h-8 text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const uploadFormData = new FormData();
                      uploadFormData.append("file", file);
                      try {
                        const response = await axios.post(`${API_URL}/upload-image`, uploadFormData, {
                          headers: { "Content-Type": "multipart/form-data" }
                        });
                        setNewDriver({ ...newDriver, license_image_url: response.data.url });
                        toast.success("License image uploaded");
                      } catch (error) {
                        toast.error("Failed to upload license image");
                      }
                    }}
                    className="hidden"
                    id="driver_license_upload"
                  />
                  <label
                    htmlFor="driver_license_upload"
                    className="flex items-center gap-2 px-3 py-1.5 bg-secondary/50 border border-border rounded-md cursor-pointer hover:bg-secondary/70 text-sm"
                  >
                    <Upload className="w-3 h-3" />
                    {newDriver.license_image_url ? "License Uploaded ✓" : "Upload License"}
                  </label>
                  <Button type="button" size="sm" variant="outline" onClick={addDriver} className="h-8">
                    <Plus className="w-3 h-3 mr-1" /> Add Driver
                  </Button>
                </div>
              </div>
            </div>

            {/* Vehicles Section */}
            <div className="p-3 bg-secondary/30 rounded-lg space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Car className="w-4 h-4" /> Vehicles ({formData.vehicles.length})
              </h4>
              
              {formData.vehicles.length > 0 && (
                <div className="space-y-2">
                  {formData.vehicles.map((vehicle, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-secondary/50 rounded text-sm">
                      <span>{vehicle.vehicle_number} - {vehicle.vehicle_type} ({vehicle.capacity})</span>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeVehicle(index)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="grid grid-cols-4 gap-2">
                <Input
                  placeholder="Vehicle No."
                  value={newVehicle.vehicle_number}
                  onChange={(e) => setNewVehicle({ ...newVehicle, vehicle_number: e.target.value.toUpperCase() })}
                  className="bg-secondary/50 h-8 text-sm"
                />
                <Input
                  placeholder="Type"
                  value={newVehicle.vehicle_type}
                  onChange={(e) => setNewVehicle({ ...newVehicle, vehicle_type: e.target.value })}
                  className="bg-secondary/50 h-8 text-sm"
                />
                <Input
                  placeholder="Capacity"
                  value={newVehicle.capacity}
                  onChange={(e) => setNewVehicle({ ...newVehicle, capacity: e.target.value })}
                  className="bg-secondary/50 h-8 text-sm"
                />
                <Button type="button" size="sm" variant="outline" onClick={addVehicle} className="h-8">
                  <Plus className="w-3 h-3 mr-1" /> Add
                </Button>
              </div>
            </div>

            {/* Bank Accounts Section */}
            <div className="p-3 bg-secondary/30 rounded-lg space-y-3" data-testid="vendor-bank-accounts-section">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Bank Accounts ({formData.bank_accounts.length})
              </h4>
              
              {formData.bank_accounts.length > 0 && (
                <div className="space-y-2">
                  {formData.bank_accounts.map((account, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-secondary/50 rounded text-sm" data-testid={`bank-account-${index}`}>
                      <div className="flex flex-col">
                        <span className="font-medium">{account.account_name || 'N/A'} - {account.bank_name}</span>
                        <span className="text-xs text-muted-foreground">A/C: {account.account_number} | IFSC: {account.ifsc_code}</span>
                      </div>
                      <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeBankAccount(index)} data-testid={`remove-bank-account-${index}`}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Account Holder Name"
                    value={newBankAccount.account_name}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, account_name: e.target.value })}
                    className="bg-secondary/50 h-8 text-sm"
                    data-testid="bank-account-name-input"
                  />
                  <Input
                    placeholder="Bank Name *"
                    value={newBankAccount.bank_name}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, bank_name: e.target.value })}
                    className="bg-secondary/50 h-8 text-sm"
                    data-testid="bank-name-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    placeholder="Account Number *"
                    value={newBankAccount.account_number}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, account_number: e.target.value })}
                    className="bg-secondary/50 h-8 text-sm"
                    data-testid="bank-account-number-input"
                  />
                  <Input
                    placeholder="IFSC Code *"
                    value={newBankAccount.ifsc_code}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, ifsc_code: e.target.value.toUpperCase() })}
                    className="bg-secondary/50 h-8 text-sm"
                    data-testid="bank-ifsc-input"
                  />
                  <Input
                    placeholder="Branch"
                    value={newBankAccount.branch}
                    onChange={(e) => setNewBankAccount({ ...newBankAccount, branch: e.target.value })}
                    className="bg-secondary/50 h-8 text-sm"
                    data-testid="bank-branch-input"
                  />
                </div>
                <Button type="button" size="sm" variant="outline" onClick={addBankAccount} className="w-full h-8" data-testid="add-bank-account-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Bank Account
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="glow-primary bg-primary" data-testid="vendor-submit-btn">
                {selectedVendor ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Reject Vendor
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting vendor "{selectedVendor?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reject_reason">Rejection Reason</Label>
              <Input
                id="reject_reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="bg-secondary/50 border-border"
                placeholder="Enter reason for rejection..."
                data-testid="vendor-reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-vendor">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete Vendor</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedVendor?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              data-testid="confirm-delete-vendor"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorsPage;
